import React, {Fragment, useState, useRef} from 'react';
import {
  NonIdealState,
  Button,
  Classes,
  Drawer,
  Intent,
  Switch,
  Dialog,
} from '@blueprintjs/core';
import {Label, TextField} from 'worksource-ui';
import {useQuery, useMutation} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';

import FilePreview from 'components/FilePreview';
import {useUIActions} from 'utils/ui-context';
import {showSuccess, showWSErrors, showGQLError} from 'utils/toaster';

const SHARE_FILES = gql`
  {
    viewerState @client {
      selectedFiles {
        id,
        name,
        url,
        thumbnailUrl,
      },
      selectingFiles,
    }
  }
`;

const TOGGLE_FILE_SELECTION = gql`
  mutation($file: SelectedFile) {
    toggleFileSelection(file: $file) @client
  }
`;

const SET_SELECTING_FILES = gql`
  mutation($value: Boolean!) {
    setSelectingFiles(value: $value) @client
  }
`;

const CREATE_SHARE_SET = gql`
mutation ShareFilesDialogCreateShareSet(
  $clientId: ID!,
  $guideId: ID!,
  $fileIds: [ID!]!,
  $creatingClientType: ClientTypeEnum!
  $shareType: ShareTypeEnum!,
  $recipientName: String!,
  $recipientEmail: String,
) {
  createShareSet(
    clientId: $clientId,
    guideId: $guideId,
    fileIds: $fileIds,
    creatingClientType: $creatingClientType,
    shareType: $shareType,
    recipientName: $recipientName,
    recipientEmail: $recipientEmail,
  ) {
    shareSet {
      recipientEmail,
      shortenedUrl,
    },
    errors {
      title,
      message,
    }
  }
}
`;

const SendLinkDialog = ({url, isOpen}) => {
  ////
  // Hooks
  //
  const textfield = useRef(null);
  const {clearDialog} = useUIActions();

  ////
  // Actions
  //
  const copyToClipboard = () => {
    textfield.current.focus();
    textfield.current.select();
    document.execCommand('copy');

    showSuccess({message: 'Copied to clipboard'});
  };

  ////
  // Render
  //
  return (
    <Dialog
      isOpen={isOpen}
      title="Share link"
      icon="link"
    >
      <div className={Classes.DRAWER_BODY}>
        <div className={Classes.DIALOG_BODY}>
          <p>
            You may use the link below to share these files with the recipient
            via your preferred medium of communication.
          </p>
          <TextField value={url} inputRef={textfield} />
        </div>
      </div>
      <div className={Classes.DRAWER_FOOTER}>
        <div className="bp3-dialog-footer-actions">
          <Button
            text="Copy to clipboard"
            onClick={copyToClipboard}
          />
          <Button
            intent={Intent.PRIMARY}
            text="Done"
            onClick={clearDialog}
          />
        </div>
      </div>
    </Dialog>
  );
};

const ShareFilesDialog = ({...props}) => {
  ////
  // Hooks
  //
  const [share, setShare] = useState(!!props.share);
  const {setDialog, clearDialog} = useUIActions();
  const [form, setForm] = useState({
    recipientName: '',
    recipientEmail: '',
    url: false,
  });

  ////
  // GraphQL
  //
  const {data: {viewerState: {selectedFiles}}} = useQuery(SHARE_FILES);
  const [toggleFileSelection] = useMutation(TOGGLE_FILE_SELECTION);
  const [setSelectingFiles] = useMutation(SET_SELECTING_FILES);
  const [
    createShareSet,
    {loading: loadingCreate},
  ] = useMutation(CREATE_SHARE_SET, {
    onCompleted: ({createShareSet: {shareSet, errors}}) => {
      if (errors.length > 0) {
        showWSErrors({errors});
      } else if (form.url) {
        setSelectingFiles({variables: {value: false}});
        setDialog(() => <SendLinkDialog url={shareSet.shortenedUrl} />);
      } else {
        setSelectingFiles({variables: {value: false}});
        showSuccess({message: `Email sent to ${shareSet.recipientEmail}`});
        clearDialog();
      }
    },
    onError: error => showGQLError({error}),
    refetchQueries: ['FetchAllUsers'],
  });

  ////
  // Events
  //
  const sendShare = () => {
    createShareSet({variables: {
      clientId: props.clientId,
      guideId: props.guideId,
      fileIds: selectedFiles.map(f => f.id),
      creatingClientType: 'web',
      shareType: form.url ? 'link' : 'email',
      recipientName: form.recipientName,
      recipientEmail: form.recipientEmail,
    }});
  };

  const removeFile = ({file}) => {
    toggleFileSelection({variables: {file}});
  };

  ////
  // Render
  //
  return (
    <Drawer
      isOpen={props.isOpen}
      onClose={clearDialog}
      icon="share"
      title={share ? 'Share files' : 'Selected Files'}
    >
      <div className={Classes.DRAWER_BODY}>
        <div className={Classes.DIALOG_BODY}>
          {share && (
            <Fragment>
              <h2>Recipient information</h2>
              <Label text="Share type">
                <div className="share-files--switch">
                  <span
                    className="share-files--switch-label"
                    style={{fontWeight: form.url ? 400 : 'bold'}}
                  >
                    Email
                  </span>
                  <Switch
                    checked={form.url}
                    onChange={e => setForm({...form, url: e.target.checked})}
                    large
                    inline
                  />
                  <span
                    className="share-files--switch-label"
                    style={{fontWeight: form.url ? 'bold' : 400}}
                  >
                    URL
                  </span>
                </div>
              </Label>
              <Label required text="Recipient Name">
                <TextField
                  value={form.recipientName}
                  placeholder="Jane Smith"
                  onChange={e => setForm({...form, recipientName: e.target.value})}
                />
              </Label>
              {!form.url && (
                <Label required text="Recipient Email">
                  <TextField
                    value={form.recipientEmail}
                    placeholder="jsmith@acme.net"
                    onChange={e => setForm({...form, recipientEmail: e.target.value})}
                  />
                </Label>
              )}
            </Fragment>
          )}

          {share && (
            <h2>Selected Files</h2>
          )}
          <div className="share-files--grid">
            {selectedFiles.map((file) => {
              return (
                <FilePreview onRemove={removeFile} key={file.id} file={file} />
              );
            })}
            {(selectedFiles.length === 0) && (
              <NonIdealState
                icon="search"
                title="No files selected"
                description="Return to the guide and select files to begin"
                action={<Button text="Close" onClick={clearDialog} />}
              />
            )}
          </div>
        </div>
      </div>
      <div className={Classes.DRAWER_FOOTER}>
        <div className="bp3-dialog-footer-actions">
          {share && (
            <Fragment>
              <Button
                onClick={clearDialog}
                disabled={loadingCreate}
                text="Cancel"
              />
              <Button
                intent={Intent.PRIMARY}
                onClick={sendShare}
                disabled={loadingCreate}
                loading={loadingCreate}
                text="Send"
              />
            </Fragment>
          )}
          {!share && (
            <Fragment>
              <Button
                onClick={clearDialog}
                disabled={loadingCreate}
                text="Close"
              />
              <Button
                intent={Intent.PRIMARY}
                onClick={() => setShare(true)}
                disabled={loadingCreate || selectedFiles.length === 0}
                loading={loadingCreate}
                text="Share files"
              />
            </Fragment>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ShareFilesDialog;
